/**
 * Domínio: Planos de Ação (GUPM-187). TS puro — sem React/Supabase/DOM.
 * Fiel ao módulo MPA do protótipo v5.10 (`PLANOS`, `updatePlanoStatus`).
 * `responsavel` é um PAPEL curado por padrão (ex.: "RT Foscentro"); a opção "Outros"
 * do form permite texto livre (com aviso de LGPD na UI) — ver TSDoc de `AcaoPlano`.
 */

/** Status de uma ação individual do plano. */
export type AcaoStatus = 'pendente' | 'andamento' | 'concluido';

/** Status do plano (derivado das ações, exceto `extinto` que é terminal explícito). */
export type PlanoStatus = 'pendente' | 'em_andamento' | 'concluido' | 'extinto';

export interface AcaoPlano {
  id: string;
  descricao: string;
  /**
   * Responsável pela execução. Por padrão é um PAPEL curado (ex.: "RT Foscentro",
   * "EoR Walm"); a opção "Outros" no form (GUPM-193) permite texto livre com aviso
   * de LGPD, então PODE conter um nome. Evitar dados sensíveis (CPF etc.).
   */
  responsavel: string;
  /** Setor responsável pela execução (categoria fixa, sem PII) — GUPM-193. */
  setor?: string | null;
  /** Prazo em ISO date (YYYY-MM-DD) ou null. */
  prazo: string | null;
  status: AcaoStatus;
  /**
   * Quando a ação foi concluída (ISO 8601 UTC). Carimbado PELO BANCO
   * (`planos_acao_carimba_conclusao`, GUPM-332) — o que o cliente envia é ignorado.
   * Ausente em ação não concluída, e em concluída cuja data não pôde ser recuperada.
   */
  concluidoEm?: string | null;
}

export interface Plano {
  id: string;
  codigo: string;
  tarpRecordId: string | null;
  /**
   * Número corrido (`seq`) da TARP que abriu o plano — o `TR-nnnn` da lista do M3.
   * `tarpCode` é só o PROTOCOLO do catálogo, igual para toda TARP do mesmo tipo.
   * `null` sem vínculo ou quando a TARP vinculada não veio na consulta.
   * Obrigatório de propósito: um produtor de `Plano` que esquecesse o campo faria
   * todo plano com vínculo virar "TARP não identificada" sem erro de compilação.
   */
  tarpSeq: number | null;
  damId: string | null;
  damCode: string;
  tarpCode: string | null;
  titulo: string;
  nivel: string | null;
  modo: string | null;
  /** Instrumento/componente associado (contexto exibido na lista de ações — GUPM-193). */
  instrumento?: string | null;
  status: PlanoStatus;
  acoes: AcaoPlano[];
  createdAt: string;
  /** Última alteração (timestamptz) — base do guard otimista do `updateAcaoStatus`. */
  updatedAt?: string | null;
}

export const PLANO_STATUS_LABEL: Record<PlanoStatus, string> = {
  pendente: 'Pendente',
  em_andamento: 'Em andamento',
  concluido: 'Concluído',
  extinto: 'Extinto',
};

export const ACAO_STATUS_LABEL: Record<AcaoStatus, string> = {
  pendente: 'Pendente',
  andamento: 'Em andamento',
  concluido: 'Concluído',
};

export const ACAO_STATUS_ICON: Record<AcaoStatus, string> = {
  pendente: '⏳',
  andamento: '🔄',
  concluido: '✅',
};

/**
 * Deriva o status do plano a partir das ações (fiel a `updatePlanoStatus` do V8):
 * sem ações OU todas pendentes → `pendente`; todas concluídas → `concluido`;
 * qualquer andamento ou conclusão parcial → `em_andamento`. `extinto` é terminal
 * explícito (declaração de extinção) e NÃO é derivado aqui.
 */
export function derivePlanoStatus(acoes: AcaoPlano[]): Exclude<PlanoStatus, 'extinto'> {
  if (acoes.length === 0) return 'pendente';
  if (acoes.every((a) => a.status === 'concluido')) return 'concluido';
  if (acoes.some((a) => a.status === 'andamento' || a.status === 'concluido')) return 'em_andamento';
  return 'pendente';
}

/**
 * Um plano é editável enquanto não for `extinto` (terminal). Guard puro e
 * testável (GUPM-187, review #104): a UI e o datasource espelham; quando a
 * "declaração de extinção" for implementada, replicar também via trigger BEFORE
 * no banco (padrão `tarp_records_write_guard`).
 */
export function isPlanoEditavel(status: PlanoStatus): boolean {
  return status !== 'extinto';
}

export interface ContagemAcoes {
  pendente: number;
  andamento: number;
  concluido: number;
  total: number;
}

/** Conta ações por status (para os stat pills do módulo). */
export function contarAcoesPorStatus(acoes: AcaoPlano[]): ContagemAcoes {
  const c: ContagemAcoes = { pendente: 0, andamento: 0, concluido: 0, total: acoes.length };
  for (const a of acoes) c[a.status]++;
  return c;
}
